import { useCallback } from 'react';

import { useTabsContext } from './useTabContext';

const focus = (element: HTMLButtonElement) => {
    element.focus();
    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
};
const useFocusTabs = <TValue = string>() => {
    const { setValue } = useTabsContext<TValue>();

    const onClickTabButton = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, value: TValue) => {
            setValue(value);
            e.currentTarget?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        },
        [setValue],
    );

    const onKeydownTabButton = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
        const buttonArray = Array.from(e.currentTarget.parentElement?.querySelectorAll('button') ?? []);
        const isLastElement = buttonArray.indexOf(e.currentTarget) === buttonArray.length - 1;
        const isFirstElement = buttonArray.indexOf(e.currentTarget) === 0;
        const lastElement = buttonArray[buttonArray.length - 1] as HTMLButtonElement | null;
        const firstElement = buttonArray[0] as HTMLButtonElement | null;
        if (e.key === 'Left' || e.key === 'ArrowLeft') {
            e.preventDefault();
            if (isFirstElement && lastElement) {
                focus(lastElement);
                return;
            }
            const prevSibling = e.currentTarget.previousSibling as HTMLButtonElement | null;
            if (prevSibling) {
                focus(prevSibling);
            }
            return;
        }
        if (e.key === 'Right' || e.key === 'ArrowRight') {
            e.preventDefault();
            if (isLastElement && firstElement) {
                focus(firstElement);
                return;
            }
            const nextSibling = e.currentTarget.nextSibling as HTMLButtonElement | null;
            if (nextSibling) {
                focus(nextSibling);
            }
            return;
        }

        if (e.key === 'Home') {
            e.preventDefault();
            if (firstElement) {
                focus(firstElement);
            }
            return;
        }

        if (e.key === 'End') {
            if (lastElement) {
                focus(lastElement);
            }
        }
    }, []);

    return { onKeydownTabButton, onClickTabButton };
};

export default useFocusTabs;
